import { yupResolver } from '@hookform/resolvers/yup';
import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { FormInput, FormSelect } from '@catalyze/ui';
import type { IBrokerDetailForm } from 'features/SignUp/types/signUpForm';
import { STEP_COUNT } from 'features/SignUp/types/signUpForm';
import { brokerDetailFormSchema } from 'schemas/signUpForm';

import {
    AVERAGE_HOME_VALUE_OPTIONS,
    HOUSES_SOLD_OPTIONS,
    YEARS_EXPERIENCE_OPTIONS,
} from './constants';
import { FormLine, InputContainer, ButtonsContainer } from './styled';

import { StyledForm } from '../../Form';
import { StyledFormButton } from '../../FormButton';
import { FormTitle } from '../../FormTitle';
import { ProgressBar } from '../../ProgressBar';

interface IBrokerDetailFormProps {
    initialValues: IBrokerDetailForm | undefined;
    onSubmit: (data: IBrokerDetailForm) => void;
    onGoBack: (data: IBrokerDetailForm) => void;
}

export const BrokerDetailForm: FC<IBrokerDetailFormProps> = ({
    onSubmit,
    onGoBack,
    initialValues,
}) => {
    const formMethods = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(brokerDetailFormSchema),
    });

    const { getValues, handleSubmit } = formMethods;

    return (
        <>
            <ProgressBar progress={(3 / STEP_COUNT) * 100} />
            <FormTitle
                goBack={() => onGoBack(getValues())}
                title="Real Estate Information"
                subtitle="Enter information about your brokerage license."
            />
            <FormProvider {...formMethods}>
                <StyledForm onSubmit={handleSubmit(onSubmit)}>
                    <FormLine>
                        <InputContainer>
                            <FormInput
                                id="brokerageLicenseNumber"
                                type="number"
                                label="License number"
                            />
                        </InputContainer>
                        <InputContainer>
                            <FormInput
                                id="brokerageName"
                                label="Brokerage name"
                            />
                        </InputContainer>
                    </FormLine>
                    <FormLine>
                        <InputContainer>
                            <FormSelect
                                id="housesSold"
                                label="Number of transactions in the last 12 months."
                                options={HOUSES_SOLD_OPTIONS}
                            />
                        </InputContainer>
                    </FormLine>
                    <FormLine>
                        <InputContainer>
                            <FormSelect
                                id="averageHomeValue"
                                label="Average transaction value."
                                options={AVERAGE_HOME_VALUE_OPTIONS}
                            />
                        </InputContainer>
                    </FormLine>
                    <FormLine>
                        <InputContainer>
                            <FormSelect
                                id="yearsExperience"
                                label="Years of experience."
                                options={YEARS_EXPERIENCE_OPTIONS}
                            />
                        </InputContainer>
                    </FormLine>
                    <ButtonsContainer>
                        <StyledFormButton type="submit">Next</StyledFormButton>
                    </ButtonsContainer>
                </StyledForm>
            </FormProvider>
        </>
    );
};
